// Product filter facets — derived from the catalogue's existing data.
//
// The catalogue follows a strict naming convention: `COUNTRY | CountryName | VENDOR | Descriptor`
// (e.g. "TH | Thailand | LSEG | eKYC 1+1"). Combined with the `category` (ISO country) and
// `vendor` fields, that lets us filter by Country, Data Source, Cascade Type and Product Type
// without any new columns or backfill. Product Type is a best-effort classification of the
// descriptor — anything that doesn't match a known bucket lands in "Other".
import type { Product } from '../types'
import { COUNTRY_OPTIONS } from '../data'

export type ProductType =
  | 'KYC'
  | 'KYB'
  | 'IDV'
  | 'Verify'
  | 'Credit'
  | 'AML/Screening'
  | 'Biometrics/Face Match'
  | 'Other'

export type CascadeType = '1+1' | '2+2' | 'None'

export interface ProductFilterState {
  countries: string[] // ISO country codes (category), e.g. ['AU', 'NZ']
  types: ProductType[]
  cascades: CascadeType[]
  dataSources: string[] // vendor values, e.g. ['LSEG', 'Socure']
}

export const EMPTY_PRODUCT_FILTER: ProductFilterState = {
  countries: [],
  types: [],
  cascades: [],
  dataSources: [],
}

// Ordered so the most specific tokens win (KYB before KYC, biometrics before generic IDV/Verify).
export function classifyProductType(name: string): ProductType {
  const n = name.toLowerCase()
  if (n.includes('kyb')) return 'KYB' // covers eKYB
  if (n.includes('kyc')) return 'KYC' // covers eKYC
  if (n.includes('face match') || n.includes('facematch') || n.includes('biometric') || n.includes('liveness') || n.includes('selfie')) {
    return 'Biometrics/Face Match'
  }
  if (n.includes('pep') || n.includes('sanction') || n.includes('adverse') || n.includes('monitoring') || n.includes('watchlist')) {
    return 'AML/Screening'
  }
  if (n.includes('credit')) return 'Credit'
  if (n.includes('idv')) return 'IDV'
  if (n.includes('verify') || n.includes('verification')) return 'Verify'
  return 'Other'
}

export function parseCascade(name: string): CascadeType {
  if (name.includes('1+1')) return '1+1'
  if (name.includes('2+2')) return '2+2'
  return 'None'
}

// Authoritative country comes from `category`; fall back to the first name segment
// for the ~2% of SKUs that don't carry a category.
export function productCountry(p: Product): string {
  if (p.category && p.category.trim()) return p.category.trim()
  const seg = p.name.split('|')[0]?.trim()
  return seg || 'Other'
}

export function productDataSource(p: Product): string {
  return (p.vendor && p.vendor.trim()) || 'Other'
}

export interface ProductFacets {
  country: string
  type: ProductType
  cascade: CascadeType
  dataSource: string
}

export function deriveFacets(p: Product): ProductFacets {
  return {
    country: productCountry(p),
    type: classifyProductType(p.name),
    cascade: parseCascade(p.name),
    dataSource: productDataSource(p),
  }
}

export function matchesProductFilter(p: Product, f: ProductFilterState): boolean {
  const { country, type, cascade, dataSource } = deriveFacets(p)
  if (f.countries.length && !f.countries.includes(country)) return false
  if (f.types.length && !f.types.includes(type)) return false
  if (f.cascades.length && !f.cascades.includes(cascade)) return false
  if (f.dataSources.length && !f.dataSources.includes(dataSource)) return false
  return true
}

export function activeFilterCount(f: ProductFilterState): number {
  return f.countries.length + f.types.length + f.cascades.length + f.dataSources.length
}

export interface FacetOption<T extends string = string> {
  value: T
  label: string
  count: number
}

const COUNTRY_LABELS: Record<string, string> = Object.fromEntries(
  COUNTRY_OPTIONS.filter((o) => o.value).map((o) => [o.value, o.label])
)

export function countryLabel(code: string): string {
  if (code === 'GLOBAL') return 'Global'
  return COUNTRY_LABELS[code] ?? code
}

// All product types in display order — shown even when a catalogue has none of a bucket
// would be noisy, so we only surface buckets that actually occur (built in buildFacetOptions).
const TYPE_ORDER: ProductType[] = ['KYC', 'KYB', 'IDV', 'Verify', 'Biometrics/Face Match', 'AML/Screening', 'Credit', 'Other']
const CASCADE_ORDER: CascadeType[] = ['1+1', '2+2', 'None']

// Build the selectable options for each facet from the products actually loaded, with counts.
export function buildFacetOptions(products: Product[]): {
  countries: FacetOption[]
  types: FacetOption<ProductType>[]
  cascades: FacetOption<CascadeType>[]
  dataSources: FacetOption[]
} {
  const countryCounts = new Map<string, number>()
  const sourceCounts = new Map<string, number>()
  const typeCounts = new Map<ProductType, number>()
  const cascadeCounts = new Map<CascadeType, number>()

  for (const p of products) {
    const f = deriveFacets(p)
    countryCounts.set(f.country, (countryCounts.get(f.country) ?? 0) + 1)
    sourceCounts.set(f.dataSource, (sourceCounts.get(f.dataSource) ?? 0) + 1)
    typeCounts.set(f.type, (typeCounts.get(f.type) ?? 0) + 1)
    cascadeCounts.set(f.cascade, (cascadeCounts.get(f.cascade) ?? 0) + 1)
  }

  const countries = [...countryCounts.entries()]
    .map(([value, count]) => ({ value, label: countryLabel(value), count }))
    .sort((a, b) => a.label.localeCompare(b.label))

  const dataSources = [...sourceCounts.entries()]
    .map(([value, count]) => ({ value, label: value, count }))
    .sort((a, b) => a.label.localeCompare(b.label))

  const types = TYPE_ORDER.filter((t) => typeCounts.has(t)).map((t) => ({
    value: t,
    label: t,
    count: typeCounts.get(t) ?? 0,
  }))

  const cascades = CASCADE_ORDER.filter((c) => cascadeCounts.has(c)).map((c) => ({
    value: c,
    label: c === 'None' ? 'No cascade' : c,
    count: cascadeCounts.get(c) ?? 0,
  }))

  return { countries, types, cascades, dataSources }
}
